import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'motion/react'
import SegmentHeader from './dynamic-creation-flow/SegmentHeader'
import SegmentPreview from './dynamic-creation-flow/SegmentPreview'
import SegmentDefinitionPanel from './dynamic-creation-flow/SegmentDefinitionPanel'
import GenericSlideInDialog from 'src/components/common/containers/GenericSlideInDialog'
import { useDynamicSegment } from 'src/lib/contexts/DynamicSegmentContext'
import type { DynamicSegmentPreviewTableHandle } from './dynamic-creation-flow/DynamicSegmentPreviewTable'

interface DynamicSegmentCreationFlowProps {
  onBack?: () => void
  onPreviewVisibilityChange?: (visible: boolean) => void
  hideHeader?: boolean
  /** Hosted views (e.g. SSO-analytics overlay) show only the segment name, no "Segments >" crumb. */
  hideBreadcrumbPrefix?: boolean
  /**
   * ShopPass overlay: slide the 440px config panel in first (over the page), then fade
   * the header + left preview in place. Original listing/create leave this off.
   */
  stagedEnter?: boolean
}

const definitionPanelShadow =
  '!shadow-[10px_80px_30px_0_rgba(0,0,0,0.15),_-40px_-20px_48px_0_rgba(250,250,250,0.80)]'

const DynamicSegmentCreationFlow: React.FC<DynamicSegmentCreationFlowProps> = ({
  onBack,
  onPreviewVisibilityChange,
  hideHeader = false,
  hideBreadcrumbPrefix = false,
  stagedEnter = false
}) => {
  const { segmentFormState } = useDynamicSegment()
  const [previewEngaged, setPreviewEngaged] = useState(false)
  const [isDefinitionPanelVisible, setIsDefinitionPanelVisible] = useState(true)
  const [sheetOpen, setSheetOpen] = useState(!stagedEnter)
  const [previewRevealed, setPreviewRevealed] = useState(!stagedEnter)
  const previewTableRef = useRef<DynamicSegmentPreviewTableHandle>(null)

  // When all conditions are removed, require clicking “Preview data” again after re-adding filters.
  // (Not derivable from props alone without resetting sticky engagement when filter count hits 0.)
  useEffect(() => {
    if (segmentFormState.filters.length === 0) {
      setPreviewEngaged(false)
    }
  }, [segmentFormState.filters.length])

  // Wide slide-in for the whole dynamic flow; short delay lets layout settle before expanding preview.
  useEffect(() => {
    const id = window.setTimeout(() => onPreviewVisibilityChange?.(true), 200)
    return () => {
      window.clearTimeout(id)
      onPreviewVisibilityChange?.(false)
    }
  }, [onPreviewVisibilityChange])

  // Open the sheet after mount so AnimatePresence treats it as an entering child (slide from x: 100%).
  useEffect(() => {
    if (!stagedEnter) return
    setSheetOpen(true)
  }, [stagedEnter])

  const setDefinitionPanelVisible = (open: boolean) => {
    if (open && !isDefinitionPanelVisible) {
      previewTableRef.current?.snapshotLayout()
    }
    if (!open) {
      previewTableRef.current?.clearLayoutSnapshot()
    }
    setIsDefinitionPanelVisible(open)
  }

  const handleToggleDefinitionPanel = () => {
    setDefinitionPanelVisible(!isDefinitionPanelVisible)
  }

  const header = !hideHeader ? (
    <SegmentHeader
      isRightPanelVisible={isDefinitionPanelVisible}
      onToggleRightPanel={handleToggleDefinitionPanel}
      onBack={onBack}
      hideBreadcrumbPrefix={hideBreadcrumbPrefix}
    />
  ) : (
    <div className='flex h-12 items-center justify-end px-5'>
      <SegmentHeader
        compactMode
        isRightPanelVisible={isDefinitionPanelVisible}
        onToggleRightPanel={handleToggleDefinitionPanel}
      />
    </div>
  )

  const preview = (
    <SegmentPreview
      ref={previewTableRef}
      isDetailPanelVisible={isDefinitionPanelVisible}
      nudgeOnly={!previewEngaged}
      onEngageFullPreview={() => setPreviewEngaged(true)}
      blurBehindContent={stagedEnter}
      backgroundTransparent={stagedEnter}
      skipEnterAnimation={stagedEnter}
      flushTop={stagedEnter}
    />
  )

  const panelBody = (
    <>
      <div
        className='h-full w-px shrink-0'
        style={{
          background: 'linear-gradient(180deg, rgba(228, 228, 231, 0.00) 0%, #E4E4E7 25%)'
        }}
      />
      <div className='min-w-0 h-full min-h-0 flex-1 bg-zinc-50'>
        <SegmentDefinitionPanel />
      </div>
    </>
  )

  if (stagedEnter) {
    return (
      <>
        <motion.div
          className='fixed top-0 right-0 z-[60] bg-zinc-50'
          style={{
            left: 'var(--sidebar-width, 0px)',
            pointerEvents: previewRevealed ? 'auto' : 'none'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: previewRevealed ? 1 : 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {header}
        </motion.div>

        <motion.div
          className='fixed bottom-0 right-0 z-40 flex min-h-0 flex-col overflow-hidden bg-zinc-50/60 backdrop-blur-md'
          style={{
            left: 'var(--sidebar-width, 0px)',
            top: '3.5rem',
            pointerEvents: previewRevealed ? 'auto' : 'none'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: previewRevealed ? 1 : 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {preview}
        </motion.div>

        <GenericSlideInDialog
          isOpen={sheetOpen && isDefinitionPanelVisible}
          setIsOpen={setDefinitionPanelVisible}
          direction='right'
          width={440}
          hideOverlay
          disableOutsideClick
          onEnterComplete={() => setPreviewRevealed(true)}
          className={`!top-14 !h-auto !rounded-none !bg-zinc-50 ${definitionPanelShadow}`}
        >
          <div className='flex h-full min-h-0 flex-row'>{panelBody}</div>
        </GenericSlideInDialog>
      </>
    )
  }

  return (
    <div className='flex h-full min-h-0 w-full flex-col'>
      {header}

      <div className='relative min-h-0 flex-1 overflow-hidden'>
        <div className='absolute inset-0 flex flex-col bg-zinc-50'>{preview}</div>
        <div
          className={`absolute top-0 right-0 bottom-0 z-20 flex w-[440px] flex-row transition-transform duration-300 ease-out ${
            isDefinitionPanelVisible ? 'translate-x-0' : 'translate-x-full'
          } ${
            isDefinitionPanelVisible
              ? 'shadow-[10px_80px_30px_0_rgba(0,0,0,0.15),_-40px_-20px_48px_0_rgba(250,250,250,0.80)]'
              : ''
          }`}
        >
          {panelBody}
        </div>
      </div>
    </div>
  )
}

export default DynamicSegmentCreationFlow
